/**
 * TabPicker UI Badge Component
 * 
 * Badge component for displaying status indicators, labels, and counts.
 * Provides multiple color variants and sizes.
 * 
 * @module TabPicker.UI.Badge
 */

import React from 'react';

/**
 * Badge component props interface
 * 
 * @interface BadgeProps
 * @property {string} [className] - Additional CSS classes
 * @property {React.ReactNode} children - Badge content
 * @property {'default' | 'success' | 'warning' | 'error' | 'info'} [variant] - Badge color variant
 * @property {'sm' | 'md' | 'lg'} [size] - Badge size
 * @property {boolean} [rounded] - Use fully rounded corners
 */
export interface BadgeProps {
  className?: string;
  children: React.ReactNode;
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info';
  size?: 'sm' | 'md' | 'lg';
  rounded?: boolean;
}

/**
 * Badge component for status indicators and labels
 * 
 * Displays small labels with colored backgrounds to indicate status,
 * categories, or counts.
 * 
 * @param {BadgeProps} props - Component props
 * @returns {JSX.Element} Badge element
 * 
 * @example
 * // Success badge
 * <Badge variant="success">Active</Badge>
 * 
 * @example
 * // Warning badge with count
 * <Badge variant="warning" size="sm">3 warnings</Badge>
 * 
 * @example
 * // Rounded pill badge
 * <Badge rounded>New</Badge>
 */
export function Badge({
  className = '',
  children,
  variant = 'default',
  size = 'md',
  rounded = false
}: BadgeProps) {
  const baseClasses = 'inline-flex items-center justify-center font-semibold transition-colors';
  
  const variantClasses = {
    default: 'bg-gray-100 text-gray-700',
    success: 'bg-green-100 text-green-700',
    warning: 'bg-amber-100 text-amber-700',
    error: 'bg-red-100 text-red-700',
    info: 'bg-blue-100 text-blue-700'
  };
  
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm',
    lg: 'px-3 py-1.5 text-base'
  };
  
  const radiusClass = rounded ? 'rounded-full' : 'rounded-md';
  
  return (
    <span className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${radiusClass} ${className}`}>
      {children}
    </span>
  );
}

/**
 * Status badge component props
 */
export interface StatusBadgeProps {
  status: 'ok' | 'warn' | 'error' | 'na';
  label: string;
  className?: string;
}

/**
 * Status badge component for displaying validation states
 * 
 * @param {StatusBadgeProps} props - Component props
 * @returns {JSX.Element} Status badge element
 * 
 * @example
 * <StatusBadge 
 *   status={holdState}
 *   label={holdState === 'ok' ? 'Healthy hold' : 'Review hold'}
 * />
 */
export function StatusBadge({ status, label, className = '' }: StatusBadgeProps) {
  const variantMap = {
    ok: 'success' as const,
    warn: 'warning' as const,
    error: 'error' as const,
    na: 'default' as const
  };
  
  return (
    <Badge variant={variantMap[status]} rounded className={className}>
      {label}
    </Badge>
  );
}

/**
 * Count badge component props
 */
export interface CountBadgeProps {
  count: number;
  maxCount?: number;
  className?: string;
}

/**
 * Count badge component for displaying numeric values
 * 
 * @param {CountBadgeProps} props - Component props
 * @returns {JSX.Element} Count badge element
 * 
 * @example
 * // Simple count
 * <CountBadge count={5} />
 * 
 * @example
 * // With max count (shows 99+)
 * <CountBadge count={150} maxCount={99} />
 */
export function CountBadge({ 
  count, 
  maxCount = 99, 
  className = '' 
}: CountBadgeProps) {
  const displayCount = count > maxCount ? `${maxCount}+` : count.toString();
  
  return (
    <Badge variant="info" size="sm" rounded className={className}>
      {displayCount}
    </Badge>
  );
}